import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import MarkdownText from '../components/MarkdownText';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  CircularProgress,
  Container,
  Fade,
  Grow,
  Button,
  Paper,
  Chip,
  Stack
} from '@mui/material';
import { 
  MenuBook as BookIcon,
  Search as SearchIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon,
  AccessTime as TimeIcon,
  Description as DocIcon
} from '@mui/icons-material';
import apiService from '../services/ApiService';

/**
 * Home page component that shows recently added/updated documents
 */
class HomePage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      documents: [],
      loading: true,
      error: null,
      stats: {
        totalDocs: 0,
        recentlyCreated: 0,
        recentlyUpdated: 0
      }
    };
  }

  /**
   * Fetch documents when component mounts
   */
  componentDidMount() {
    this.fetchDocuments();
  }

  /**
   * Fetch documents from the API
   */
  async fetchDocuments() {
    try {
      const response = await apiService.getDocuments();
      const allDocuments = response.data;
      
      // Sort by _ts (creation timestamp) and take only the 12 most recent
      const sortedDocs = allDocuments.sort((a, b) => {
        const aTime = a._ts || new Date(a.createdAt).getTime() / 1000;
        const bTime = b._ts || new Date(b.createdAt).getTime() / 1000;
        return bTime - aTime;
      });
      const recentDocuments = sortedDocs.slice(0, 12);
      
      // Calculate stats using _ts when available
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneWeekAgoTs = Math.floor(oneWeekAgo.getTime() / 1000);
      
      const recentlyCreated = allDocuments.filter(doc => {
        const creationTime = doc._ts || Math.floor(new Date(doc.createdAt).getTime() / 1000);
        return creationTime > oneWeekAgoTs;
      }).length;
      
      const recentlyUpdated = allDocuments.filter(doc => {
        const updateTime = Math.floor(new Date(doc.updatedAt).getTime() / 1000);
        const creationTime = doc._ts || Math.floor(new Date(doc.createdAt).getTime() / 1000);
        return updateTime > oneWeekAgoTs && creationTime <= oneWeekAgoTs;
      }).length;
      
      this.setState({ 
        documents: recentDocuments, 
        loading: false,
        stats: {
          totalDocs: allDocuments.length,
          recentlyCreated,
          recentlyUpdated
        }
      });
    } catch (error) {
      this.setState({ 
        error: 'Failed to load documents. Please try again later.', 
        loading: false 
      });
    }
  }

  /**
   * Format date to readable string
   * @param {String} dateString - ISO date string
   * @returns {String} Formatted date
   */
  formatDate(dateString) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  /**
   * Get relative time string
   * @param {String} dateString - ISO date string
   * @returns {String} Relative time
   */
  getRelativeTime(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just updated';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return this.formatDate(dateString);
  }

  /**
   * Get relative time string from timestamp
   * @param {Number|String} timestamp - Unix timestamp or ISO date string
   * @returns {String} Relative time
   */
  getRelativeTimeFromTs(timestamp) {
    const now = new Date();
    let date;
    
    // Handle both _ts (unix timestamp) and ISO date strings
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
    } else {
      date = new Date(timestamp);
    }
    
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just created';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  }

  /**
   * Get creation timestamp - prefer _ts over createdAt
   * @param {Object} document - Document object
   * @returns {Number|String} Timestamp
   */
  getCreationTimestamp(document) {
    return document._ts || document.createdAt;
  }

  render() {
    const { documents, loading, error, stats } = this.state;

    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 3, sm: 4 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              px: { xs: 1, sm: 0 }
            }}
          >
            Explore the newest additions to our knowledge base and see what's been updated recently.
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2, sm: 3 }} 
            justifyContent="center"
            sx={{ mb: { xs: 4, sm: 6 } }}
          >
            <Button
              component={RouterLink}
              to="/search"
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6c0c46 30%, #8e3a6d 90%)',
                color: 'white',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a0a3a 30%, #7a325d 90%)',
                },
              }}
            >
              Search Knowledge
            </Button>
            <Button
              component={RouterLink}
              to="/documents/new"
              variant="outlined"
              size="large"
              startIcon={<AddIcon />}
              sx={{
                borderColor: '#6c0c46',
                color: '#6c0c46',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&:hover': {
                  borderColor: '#5a0a3a',
                  backgroundColor: 'rgba(108, 12, 70, 0.04)',
                },
              }}
            >
              Add Document
            </Button>
          </Stack>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, sm: 6 } }}>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                textAlign: 'center',
                background: 'linear-gradient(135deg, #6c0c46 0%, #8e3a6d 100%)',
                color: 'white',
                borderRadius: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <DocIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {stats.totalDocs}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Total Documents
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                textAlign: 'center',
                background: 'linear-gradient(135deg, #3d7185 0%, #6ca0b6 100%)',
                color: 'white',
                borderRadius: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <TrendingIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {stats.recentlyCreated}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Created This Week
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                textAlign: 'center',
                background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                color: 'white',
                borderRadius: 2,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <TimeIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {stats.recentlyUpdated}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Updated This Week
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Documents Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Recently Added
            </Typography>
            <Chip 
              label={`Latest ${documents.length}`} 
              variant="outlined" 
              color="primary"
              size="small"
              sx={{ 
                borderColor: 'rgba(108, 12, 70, 0.3)',
                color: '#6c0c46',
                fontSize: '0.8rem'
              }}
            />
          </Box>
          
          {loading ? (
            <Fade in>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                <CircularProgress size={60} thickness={4} />
              </Box>
            </Fade>
          ) : error ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
              <Typography variant="h6">{error}</Typography>
            </Paper>
          ) : documents.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
              <BookIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No documents yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Be the first to contribute to the knowledge base!
              </Typography>
              <Button
                component={RouterLink}
                to="/documents/new"
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
              >
                Create First Document
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {documents.map((document, index) => (
                <Grow
                  key={document.id}
                  in
                  timeout={1000 + (index * 100)}
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease-in-out',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(108, 12, 70, 0.15)',
                        },
                      }}
                    >
                      <CardActionArea
                        component={RouterLink}
                        to={`/documents/${document.id}`}
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          p: 0
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 600,
                              color: '#6c0c46',
                              fontSize: { xs: '1rem', sm: '1.25rem' },
                              lineHeight: 1.3
                            }}
                          >
                            <MarkdownText>{document.Term || 'Untitled'}</MarkdownText>
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            <TimeIcon fontSize="small" />
                            {this.getRelativeTimeFromTs(this.getCreationTimestamp(document))}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: { xs: 2, sm: 3 },
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4,
                              fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                          >
                            <MarkdownText>{document.Definition || 'No description available'}</MarkdownText>
                          </Typography>
                          
                          {document.tags && document.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                              {document.tags.slice(0, 3).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                    height: { xs: 20, sm: 24 }
                                  }}
                                />
                              ))}
                              {document.tags.length > 3 && (
                                <Chip
                                  label={`+${document.tags.length - 3}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                    height: { xs: 20, sm: 24 }
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                </Grow>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    );
  }
}

export default HomePage;
